import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../contexts/CognitoAuthContext";
import { ROUTES } from "../../constants/routes";
import PalliumPageBackground from "../layout/PalliumPageBackground";
import PalliumCard from "../layout/PalliumCard";

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Incorrect email or password");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // If user is already authenticated, redirect to accounts page
    // Only redirect if we're not currently in a loading state
    if (isAuthenticated && !isLoading) {
      navigate(ROUTES.ACCOUNTS, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (showPasswordChange) {
      // Handle password change
      if (newPassword !== confirmPassword) {
        setErrorMessage("New passwords do not match");
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
        }, 5000);
        return;
      }
      
      if (newPassword.length < 8) {
        setErrorMessage("Password must be at least 8 characters long");
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
        }, 5000);
        return;
      }
      
      try {
        const result = await signIn(email, password, newPassword);
        // Navigation will be handled by useEffect when isAuthenticated changes
        if (!result.isSignedIn) {
          // Handle any non-success states if needed
        }
      } catch(e) {
        console.error('Password change error:', e);
        setErrorMessage("Failed to change password. Please ensure it meets all requirements.");
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
        }, 5000);
      }
    } else {
      // Normal login
      try {
        const result = await signIn(email, password);
        
        if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
          // Show password change form
          setShowPasswordChange(true);
          setErrorMessage("");
          setShowError(false);
        } else if (result.isSignedIn) {
          // Navigation will be handled by useEffect when isAuthenticated changes
          // Just clear any error states
          setErrorMessage("");
          setShowError(false);
        }
      } catch(e) {
        console.error('Login error:', e);
        setErrorMessage("Incorrect email or password");
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
        }, 5000);
      }
    }
  };

  // Input field styles
  const inputBaseStyle = {
    border: '1px solid var(--pallium-border-default)',
    borderRadius: 'var(--pallium-border-radius-md)',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = 'var(--pallium-border-focus)';
    e.target.style.boxShadow = 'var(--pallium-focus-ring-primary)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'var(--pallium-border-default)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <PalliumPageBackground>
      <PalliumCard centered width="narrow" padding="large">
        <div className="flex justify-center mb-6">
          <img 
            src="/pallium-logo-blue.svg" 
            alt="Pallium Logo" 
            className="h-12 w-auto"
          />
        </div>
        <h2 
          className="text-2xl font-bold text-center mb-6"
          style={{ color: 'var(--pallium-primary-dark-navy)' }}
        >
          {showPasswordChange ? "Set New Password" : "Sign in to Pallium"}
        </h2>
        {showPasswordChange && (
          <p 
            className="text-sm text-center mb-4"
            style={{ color: 'var(--pallium-text-secondary)' }}
          >
            Your password needs to be changed. Please set a new password.
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-5">
          {!showPasswordChange ? (
            <>
              <div>
                <label 
                  className="block mb-1 text-sm font-medium"
                  style={{ color: 'var(--pallium-text-tertiary)' }}
                >
                  Email address
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 focus:outline-none"
                  style={inputBaseStyle}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  required
                />
              </div>
              <div>
                <label 
                  className="block mb-1 text-sm font-medium"
                  style={{ color: 'var(--pallium-text-tertiary)' }}
                >
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full px-4 py-3 pr-12 focus:outline-none"
                    style={inputBaseStyle}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.6,
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.opacity = '1'}
                    onMouseOut={(e) => e.target.style.opacity = '0.6'}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                        <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                        <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                        <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <label 
                  className="block mb-1 text-sm font-medium"
                  style={{ color: 'var(--pallium-text-tertiary)' }}
                >
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="w-full px-4 py-3 pr-12 focus:outline-none"
                    style={inputBaseStyle}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="Must be at least 8 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.6,
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.opacity = '1'}
                    onMouseOut={(e) => e.target.style.opacity = '0.6'}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                        <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                        <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                        <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label 
                  className="block mb-1 text-sm font-medium"
                  style={{ color: 'var(--pallium-text-tertiary)' }}
                >
                  Confirm New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="w-full px-4 py-3 pr-12 focus:outline-none"
                    style={inputBaseStyle}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.6,
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.opacity = '1'}
                    onMouseOut={(e) => e.target.style.opacity = '0.6'}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                        <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                        <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ color: 'var(--pallium-text-secondary)' }}>
                        <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                        <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div 
                className="text-xs"
                style={{ color: 'var(--pallium-text-secondary)' }}
              >
                Password must contain:
                <ul className="list-disc list-inside mt-1">
                  <li>At least 8 characters</li>
                  <li>Uppercase and lowercase letters</li>
                  <li>At least one number</li>
                  <li>At least one special character</li>
                </ul>
              </div>
            </>
          )}
          {!showPasswordChange && (
            <div className="text-right">
              <button 
                type="button"
                className="text-sm cursor-pointer hover:underline" 
                style={{ color: 'var(--pallium-link-default)' }}
                onClick={() => navigate('/forgot-password')}
                onMouseOver={(e) => e.target.style.color = 'var(--pallium-link-hover)'}
                onMouseOut={(e) => e.target.style.color = 'var(--pallium-link-default)'}
              >
                Forgot password?
              </button>
            </div>
          )}
          {showError && (
            <div 
              className="p-3 rounded-md text-sm"
              style={{ 
                backgroundColor: 'var(--pallium-alert-error-bg)', 
                color: 'var(--pallium-error-red)',
                border: '1px solid var(--pallium-error-red)'
              }}
            >
              {errorMessage}
            </div>
          )}
          <button
            type="submit"
            className="w-full py-3 rounded-md text-white font-medium transition-colors duration-200"
            style={{ 
              backgroundColor: 'var(--pallium-button-primary-bg)',
              borderRadius: 'var(--pallium-border-radius-md)'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = 'var(--pallium-button-primary-hover)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'var(--pallium-button-primary-bg)'}
          >
            {showPasswordChange ? "Set New Password" : "Sign In"}
          </button>
          {showPasswordChange && (
            <button
              type="button"
              onClick={() => {
                setShowPasswordChange(false);
                setNewPassword("");
                setConfirmPassword("");
              }}
              className="w-full py-2 transition-colors duration-200"
              style={{ color: 'var(--pallium-text-secondary)' }}
              onMouseOver={(e) => e.target.style.color = 'var(--pallium-text-tertiary)'}
              onMouseOut={(e) => e.target.style.color = 'var(--pallium-text-secondary)'}
            >
              Back to Login
            </button>
          )}
        </form>
      </PalliumCard>
    </PalliumPageBackground>
  );
};

export default Login;
